import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import GoBackButton from "@/common/components/GoBackButton";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import SelectController from "@/common/components/SelectController";
import Spinner from "@/common/components/Spinner";
import useBlobs from "@/common/hooks/queries/useBlobs";
import useCompany from "@/common/hooks/queries/useCompany";
import useDebounce from "@/common/hooks/useDebounce";
import { EquipmentsTransformer } from "@/common/services/equipmentTransformer";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ApplicableProducts } from "@/features/privateCompanies/components/createEquipmentForm/applicableProducts";
import { EquipmentDetails } from "@/features/privateCompanies/components/createEquipmentForm/equipmentDetails";
import { PeriodRestriction } from "@/features/privateCompanies/components/createEquipmentForm/periodRestriction";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import useCreateCompanyEquipment from "@/features/privateCompanies/hooks/useCreateCompanyEquipment";
import { companyEquipmentSchema, CompanyEquipmentValidationSchema } from "@/features/privateCompanies/schemas/createEquipment.schema";
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type";
import { companyBranchDefaultPeriodRestrictionData } from "@/features/privateCompanies/utils/companyBranchDefaultPeriodRestrictionData";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsRightIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

const Create = () => {
  const { id: companyId } = useParams()
  const queryClient = useQueryClient()

  const [searchBranch, setSearchBranch] = useDebounce('');
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();
  const navigate = useNavigate();

  const { data: company } = useCompany({ id: companyId || '' })

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors }
  } = useForm<CompanyEquipmentValidationSchema>({
    resolver: zodResolver(companyEquipmentSchema),
    defaultValues: {
      period_restrictions_attributes: companyBranchDefaultPeriodRestrictionData(),
    }
  })

  const {
    mutate: createCompanyEquipment,
    isPending: createCompanyEquipmentIsPending,
    isError: createCompanyEquipmentIsError,
  } = useCreateCompanyEquipment({
    companyId,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipments'] })
      const equipmentId = response.data.data.id;
      navigate(`/companies/${companyId}/equipments/${equipmentId}`);
    },
    onError: FormTransformers.errorsTransformer<CompanyEquipmentValidationSchema>({
      setError,
      setMessages,
      transformer: EquipmentsTransformer.errorsTransformer(),
    }),
  })

  const onSubmit: SubmitHandler<CompanyEquipmentValidationSchema> = async (data) => {
    createCompanyEquipment(data)
  }

  const documents = watch('documents') || []
  const equipmentTier = watch('equipment_tier')

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-x-4 items-center">
          <GoBackButton fallback={`/companies/${companyId}/equipments`} />
          <p className="text-white-400 font-normal">{company?.general_information?.fantasy_name}</p>
          <ChevronsRightIcon color='#ffffffb3' />
          <h2>Novo equipamento</h2>
        </div>
        <div className="flex gap-x-4 items-center">
          <Link to={`/companies/${companyId}/equipments`}>
            <Button variant="outline-error">Cancelar</Button>
          </Link>
          <Button name="submit" type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" disabled={uploadBlobIsPending}>
            {createCompanyEquipmentIsPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Criar'}
          </Button>
        </div>
      </div>
      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
        <p className="text-lg font-bold">Dados do equipamento</p>
        <MultipleImageUploader
          label="Anexe uma foto"
          name="equipment-images"
          accept="image/jpeg, image/png"
          uploadBlob={uploadBlob}
          control={control}
          fieldName="photos"
          disabled={uploadBlobIsPending || createCompanyEquipmentIsPending}
          errorMessage={errors.photos?.message || undefined}
        />
        <SelectController
          control={control}
          name="branch_id"
          label="Filial"
          markAsRequired
          options={companyBranchOptions || []}
          onInputChange={setSearchBranch}
          errorMessage={errors.branch_id?.message || undefined}
          isDisabled={createCompanyEquipmentIsPending}
        />
        <PeriodRestriction
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={createCompanyEquipmentIsPending}
        />
        <EquipmentDetails
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={createCompanyEquipmentIsPending}
        />
        <Accordion
          head={
            <Checkbox
              label="Documentos do equipamento"
              name="equipment_documents"
              boxClassName="w-[24px] h-[24px]"
              checked={documents.length > 0}
              disabled
            />
          }
          body={(
            <div className="flex w-full flex-col gap-4 text-white-800">
              <MultipleDocumentUploader
                title="Anexe um documento do equipamento"
                name="equipment-documents"
                fieldName="documents"
                accept="application/pdf"
                uploadBlob={uploadBlob}
                control={control}
                disabled={uploadBlobIsPending || createCompanyEquipmentIsPending}
              />
            </div>
          )}
        />
        <ApplicableProducts
          equipmentTierId={equipmentTier?.value}
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={createCompanyEquipmentIsPending}
          hideProductConsumption
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={createCompanyEquipmentIsError}
        />
      )}
    </form>
  )
}

export default Create
